/**
 * Janela de resposta da escuta local (spec 023, fase 3, pedido do Leopoldo de
 * 23/10/2026): a conferência pergunta "Imprimo?" e o médico responde de
 * longe — "sim", "pula", "todas", "repete", "para".
 *
 * Quem pergunta (`PrintConferenceOverlay`) pede a janela aqui; quem ouve é o
 * `WakeWordWatcher`, com o mesmo reconhecedor do "Atlas" (motor do Chrome ou
 * o próprio). Pedido pendente liga a escuta mesmo desligada nesta máquina,
 * como a tecla do Atlas; ela só abre a janela quando o reconhecedor está de
 * fato ouvindo (o bipe curto é o "pode falar") e desliga depois.
 *
 * Por que não o Ao vivo: ele fica pausado na conferência (a voz do sistema
 * sai na caixa e o microfone ouviria), custa token e tem latência — e um
 * "sim" solto na conversa com o paciente não pode virar impressão. A janela
 * curta logo depois da pergunta é o que dá contexto ao "sim"; frase comprida
 * (conversa) não vale como resposta.
 */

import { useSyncExternalStore } from 'react';
import { normalizeSpeech } from './matcher';

export type RespostaConferencia = 'imprimir' | 'pular' | 'todas' | 'repetir' | 'parar';

/**
 * O que o ouvido português do motor próprio entende na janela (fora isso,
 * `[unk]`). Toda forma daqui vira resposta — o teste confere. Sinônimo novo
 * entra AQUI e em `respostaDaConferencia`.
 */
export const RESPOSTAS_CONFERENCIA_VOSK = [
  // imprimir
  'sim',
  'pode',
  'pode imprimir',
  'imprime',
  'imprima',
  'confirma',
  'confirmo',
  'isso',
  // pular
  'não',
  'pula',
  'pular',
  'pode pular',
  // todas
  'todas',
  'imprime todas',
  'todos',
  // repetir
  'repete',
  'repita',
  'de novo',
  // parar
  'para',
  'pare',
  'parar',
  'cancela',
  'cancelar',
];

/** Mais que isso é conversa, não resposta ("sim, ele vai usar os óculos pra longe"). */
const MAX_PALAVRAS = 4;

/**
 * A resposta dita, ou `null`. Na dúvida, o que NÃO imprime ganha: "não pode
 * imprimir" é pular; "pode parar", parar.
 */
export function respostaDaConferencia(texto: string): RespostaConferencia | null {
  const t = normalizeSpeech(texto).replace(/\b(atlas|jarvis)\b/g, ' ').replace(/\s+/g, ' ').trim();
  const palavras = t ? t.split(' ') : [];
  if (palavras.length === 0 || palavras.length > MAX_PALAVRAS) return null;
  const tem = (...ws: string[]) => palavras.some((w) => ws.includes(w));
  if (tem('para', 'pare', 'parar', 'cancela', 'cancelar')) return 'parar';
  if (tem('nao', 'pula', 'pular')) return 'pular';
  if (tem('todas', 'todos')) return 'todas';
  if (tem('repete', 'repita') || /\bde novo\b/.test(t)) return 'repetir';
  if (tem('sim', 'pode', 'imprime', 'imprima', 'imprimir', 'confirma', 'confirmo', 'isso', 'ok')) return 'imprimir';
  return null;
}

// ─── O pedido de janela ───

/** Quanto a janela fica aberta (depois do bipe), por padrão. */
export const RESPOSTA_MS = 7000;
/** Escuta que não fica pronta nisso (motor carregando, travada): desiste. */
const RESPOSTA_ABRIR_MAX_MS = 15_000;

/** `silencio` = ninguém respondeu; `indisponivel` = a escuta não abriu. */
export type FimResposta = 'silencio' | 'cancelada' | 'indisponivel';

export interface PedidoResposta {
  /** Quanto a janela fica aberta depois que o reconhecedor está ouvindo. */
  ms: number;
  /** Frase ouvida na janela; `true` = era a resposta (a janela fecha). */
  aoOuvir: (texto: string) => boolean;
  /** A janela fechou sem resposta. */
  aoTerminar?: (motivo: FimResposta) => void;
}

let atual: PedidoResposta | null = null;
let aberta = false;
let desistir: ReturnType<typeof setTimeout> | null = null;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((f) => f());

/** Fecha o pedido (`motivo` vazio = foi respondido). */
export function encerrarResposta(p: PedidoResposta, motivo?: FimResposta): void {
  if (atual !== p) return;
  atual = null;
  aberta = false;
  if (desistir) clearTimeout(desistir);
  desistir = null;
  avisar();
  if (motivo) p.aoTerminar?.(motivo);
}

/** Pede a janela; devolve o "cancelar" (folha trocou, caneta, tela fechou). */
export function pedirResposta(p: PedidoResposta): () => void {
  if (atual) encerrarResposta(atual, 'cancelada');
  atual = p;
  aberta = false;
  desistir = setTimeout(() => {
    if (atual === p && !aberta) encerrarResposta(p, 'indisponivel');
  }, RESPOSTA_ABRIR_MAX_MS);
  avisar();
  return () => encerrarResposta(p, 'cancelada');
}

export function getRespostaPendente(): PedidoResposta | null {
  return atual;
}

/** Quem ouve abriu a janela (o reconhecedor está ouvindo). */
export function marcarRespostaAberta(p: PedidoResposta): void {
  if (atual !== p) return;
  aberta = true;
  if (desistir) clearTimeout(desistir);
  desistir = null;
}

export function assinarResposta(f: () => void): () => void {
  ouvintes.add(f);
  return () => ouvintes.delete(f);
}

/** Há pedido de janela? (Liga a escuta mesmo desligada nesta máquina.) */
export function useRespostaPendente(): boolean {
  return useSyncExternalStore(assinarResposta, () => atual !== null, () => false);
}
